import React, { useState, useEffect } from "react";
import "../AddMedicalStore/addMedicalStore.css"; // Keeping the same styling
import axios from "axios";
import JoditEditor from "jodit-react";

const AddDoctor = () => {
  const [doctorData, setDoctorData] = useState({
    name: "",
    specialization: "",
    mobileNum: "",
    hospitalIds: [], // Store selected hospital IDs
    image: null, // File input for image
    about: "",
  });

  const [hospitals, setHospitals] = useState([]); // Store hospital list
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [specializations, setSpecialization] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false); // Dropdown visibility

  useEffect(() => {
    // Fetch hospitals when component loads
    const fetchHospitals = async () => {
      try {
        const response = await axios.get(
          "https://api.swasthmitra.com/api/hospital/gethospital"
        );
        setHospitals(response.data.data);
      } catch (error) {
        setMessage({ text: "Error fetching hospitals.", type: "error" });
      }
    };
    fetchHospitals();
  }, []);

  useEffect(() => {
    // Fetch specializations
    const fetchSpecializations = async () => {
      try {
        const response = await axios.get(
          "https://api.swasthmitra.com/api/doctorCategory/get"
        );
        setSpecialization(response.data.categories);
      } catch (error) {
        console.error("Error fetching specializations:", error);
      }
    };
    fetchSpecializations();
  }, []);

  // Handle checkbox selection
  const handleCheckboxChange = (hospitalId) => {
    setDoctorData((prevData) => {
      const isSelected = prevData.hospitalIds.includes(hospitalId);
      return {
        ...prevData,
        hospitalIds: isSelected
          ? prevData.hospitalIds.filter((id) => id !== hospitalId)
          : [...prevData.hospitalIds, hospitalId],
      };
    });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    setDoctorData({ ...doctorData, image: e.target.files[0] });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (
      !doctorData.name ||
      !category ||
      !doctorData.mobileNum ||
      doctorData.hospitalIds.length === 0 ||
      !doctorData.image ||
      !doctorData.about
    ) {
      setMessage({ text: "All fields are required!", type: "error" });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", doctorData.name);
    formData.append("specialization", category);
    formData.append("mobileNum", doctorData.mobileNum);
    doctorData.hospitalIds.forEach((id) =>
      formData.append("hospitalIds[]", id)
    );
    formData.append("image", doctorData.image);
    formData.append("about", doctorData.about);

    try {
      const response = await axios.post(
        "https://api.swasthmitra.com/api/doctor/adddoctor",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.status === 201) {
        setMessage({ text: "Doctor added successfully!", type: "success" });
        setDoctorData({
          name: "",
          specialization: "",
          mobileNum: "",
          hospitalIds: [],
          image: null,
          about: "",
        });
      } else {
        setMessage({
          text: response.data.message || "Error adding doctor",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage({
        text: "An error occurred. Please try again.",
        type: "error",
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="doctor-form-container">
      <h2 className="doctor-form-title">Add Doctor</h2>

      {message && (
        <div className={`doctor-form-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form
        className="doctor-form"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        <div className="doctor-form-group">
          <label htmlFor="name">Doctor's Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={doctorData.name}
            onChange={(e) =>
              setDoctorData({ ...doctorData, name: e.target.value })
            }
            placeholder="Enter doctor's name"
          />
        </div>

        <div className="doctor-form-group">
          <label htmlFor="specialization">Specialization</label>
          <select
            id="specialization"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
          >
            <option value="">Select Specialization</option>
            {specializations?.map((cat, index) => (
              <option key={index} value={cat.slug || cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="doctor-form-group">
          <label htmlFor="mobileNum">Mobile Number</label>
          <input
            type="text"
            id="mobileNum"
            name="mobileNum"
            value={doctorData.mobileNum}
            onChange={(e) =>
              setDoctorData({ ...doctorData, mobileNum: e.target.value })
            }
            placeholder="Enter mobile number"
          />
        </div>

        <div className="doctor-form-group">
          <label>Select Hospitals</label>
          <div className="doctor-dropdown-container">
            <button
              type="button"
              className="doctor-dropdown-button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {doctorData.hospitalIds.length > 0
                ? `${doctorData.hospitalIds.length} Selected`
                : "Select Hospitals"}
            </button>
            {dropdownOpen && (
              <div className="doctor-dropdown-menu">
                {hospitals.map((hospital) => (
                  <label key={hospital._id} className="doctor-dropdown-item">
                    <input
                      className="doctor-checkbox"
                      type="checkbox"
                      value={hospital._id}
                      name={hospital._id}
                      checked={doctorData.hospitalIds.includes(hospital._id)}
                      onChange={() => handleCheckboxChange(hospital._id)}
                    />
                    <span className="doctor-hospital-name">
                      {hospital.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="doctor-form-group">
          <label htmlFor="about">About Doctor</label>
          <JoditEditor
            value={doctorData.about}
            onChange={(newContent) =>
              setDoctorData({ ...doctorData, about: newContent })
            }
          />
        </div>

        <div className="doctor-form-group">
          <label htmlFor="image">Upload Doctor's Image</label>
          <input
            type="file"
            id="image"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        <button
          type="submit"
          className="doctor-form-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? "Adding Doctor..." : "Add Doctor"}
        </button>
      </form>
    </div>
  );
};

export default AddDoctor;
